/**
 * The register merge used by BOTH sides of sync (sync.md §2, §5 steps 3–6, §6): the device applies
 * pulled changes with it, the server applies pushed ones.
 *
 * Per incoming register with Hlc `h` against the stored Hlc `s`: `h > s` overwrites every column of
 * the register and its Hlc; `h == s` is a no-op; `h < s` is ignored ("stale"). A row the receiver does
 * not have must arrive whole (every register), else it is rejected `missing_base`.
 *
 * Message rows are written register by register and then made consistent as ONE function of their
 * final registers, so the order in which registers arrive never matters:
 *   - final `deleted_at` set (a stub)  → the stub cascade (storage/cascade.ts): nullable content
 *     nulled, FTS row removed, child rows deleted. This also runs when only the content register was
 *     written to an existing stub, so a stub's content columns are always null on every replica,
 *     whatever order the content and life writes arrived in;
 *   - live and content or life written → the FTS row is rebuilt from the content.
 * Child rows (`parentMessageColumn`) of a stub or an unknown message are dropped and reported as
 * `orphans`, so each side can react (server: `missing_base`; device: re-pull later, see sync/index.ts).
 *
 * Everything runs in one transaction with `sync_meta.applying = '1'`, so the dirty-marking
 * triggers stay silent: a remote change is never pushed back.
 *
 * Shown scores (data schema 3, models.md §4.3 caller 3): after the batch, every live message whose
 * `message_scores` rows or `auto` register were written gets its shown columns recomputed from its rows
 * (`recomputeShownApplied`, in place, auto_hlc kept); when a `models` row was written (the order may have
 * changed), so does every message with more than one scored row. Every replica derives the same columns
 * from the same converged rows, which is what makes them converge.
 */

import type { EpochMs } from '../contracts/ids.js';
import type { SqlDriver, SqlValue } from '../contracts/storage.js';
import { SYNC_TABLES, type ApplyResult, type PushRejectReason, type RowChange, type SyncTableName, type SyncTableSpec } from '../contracts/sync.js';
import type { ApplyChanges } from '../contracts/wiring.js';
import { DigesterError } from '../kernel/errors.js';
import { hlcMs, isHlc } from '../kernel/hlc.js';
import { applyStubCascade, clearStubShown } from '../storage/cascade.js';
import {
  insertSyncedRow, keyFromWireId, messageBucketSource, messageLife, selectSyncedRow, updateSyncedRegister,
} from '../storage/ext/sync/index.js';
import { syncFtsRow } from '../storage/fts.js';
import { messagesWithSeveralVectors, recomputeShownApplied } from '../storage/shown.js';
import { MESSAGE_CHILD_TABLES, TABLE_META, type TableMeta } from '../storage/meta.js';
import { MAX_TRACKED_MESSAGE_IDS, type BucketRef } from '../storage/store.js';
import { withApplying } from '../storage/sync-meta.js';

const DAY_MS = 86_400_000;

/** Tables only the server writes (sync.md §1). */
export const SERVER_OWNED_TABLES: ReadonlySet<string> = new Set(['connectors']);

const TABLE_ORDER: ReadonlyMap<string, number> = new Map((SYNC_TABLES as readonly SyncTableSpec[]).map((s, i) => [s.table, i]));

export interface MergeOptions {
  now: EpochMs;
  /** null = no skew check (a device applying the server's changes). */
  maxClockSkewMs: number | null;
  allowServerOwnedTables: boolean;
  /** Called once per register actually written (the server sequences it). */
  onWritten?: (table: string, id: string, register: string) => void;
}

/** The full outcome of a merge: the contract's ApplyResult plus what the callers need afterwards. */
export interface MergeOutcome extends ApplyResult {
  /** True when more than MAX_TRACKED_MESSAGE_IDS messages changed (`messageIds` is then truncated). */
  messageOverflow: boolean;
  /** The largest valid Hlc among the incoming registers (receive rule), or null. */
  maxHlc: string | null;
  /** Usefulness buckets whose inputs changed (engine.md §14). */
  buckets: BucketRef[];
  /**
   * Child rows dropped because their parent message is a stub or unknown here (NOT counted in
   * `stale`). The server turns them into `missing_base` rejections, the device decides whether to
   * pull them again (sync/index.ts).
   */
  orphans: Orphan[];
}

/** A child row whose parent message was not live on the receiver. */
export interface Orphan {
  t: SyncTableName;
  id: string;
  parentId: string;
  parent: 'stub' | 'unknown';
}

type Reject = { t: SyncTableName; id: string; reason: PushRejectReason };

/** A row change that passed validation. */
interface ValidChange {
  meta: TableMeta;
  id: string;
  key: SqlValue[];
  regs: Array<{ name: string; h: string; v: Record<string, SqlValue> }>;
}

function isSqlScalar(value: unknown): value is SqlValue {
  return value === null || typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value));
}

/** Validate one incoming change; returns a reject reason or the parsed change. */
function validate(
  db: SqlDriver,
  change: unknown,
  options: Pick<MergeOptions, 'now' | 'maxClockSkewMs' | 'allowServerOwnedTables'>,
): { reject: PushRejectReason; t: string; id: string } | { change: ValidChange } {
  const c = (typeof change === 'object' && change !== null ? change : {}) as Partial<RowChange>;
  const t = typeof c.t === 'string' ? c.t : '';
  const id = typeof c.id === 'string' ? c.id : '';
  const meta = TABLE_META.get(t);
  if (!meta) return { reject: 'invalid', t, id };
  if (SERVER_OWNED_TABLES.has(t) && !options.allowServerOwnedTables) return { reject: 'not_writable', t, id };
  const key = keyFromWireId(db, meta, id);
  if (!key) return { reject: 'invalid', t, id };
  if (typeof c.regs !== 'object' || c.regs === null || Array.isArray(c.regs)) return { reject: 'invalid', t, id };
  const regs: ValidChange['regs'] = [];
  let skewed = false;
  for (const reg of meta.registers) {
    const incoming = (c.regs as Record<string, unknown>)[reg.name];
    if (incoming === undefined) continue;
    if (typeof incoming !== 'object' || incoming === null) return { reject: 'invalid', t, id };
    const { h, v } = incoming as { h?: unknown; v?: unknown };
    if (!isHlc(h) || typeof v !== 'object' || v === null || Array.isArray(v)) return { reject: 'invalid', t, id };
    const values = v as Record<string, unknown>;
    const names = Object.keys(values);
    if (names.length !== reg.columns.length || !reg.columns.every((col) => col in values)) return { reject: 'invalid', t, id };
    const clean: Record<string, SqlValue> = {};
    for (const col of reg.columns) {
      const value = values[col];
      if (!isSqlScalar(value)) return { reject: 'invalid', t, id };
      clean[col] = value;
    }
    if (options.maxClockSkewMs !== null && hlcMs(h) > options.now + options.maxClockSkewMs) skewed = true;
    regs.push({ name: reg.name, h, v: clean });
  }
  const known = new Set(meta.registers.map((r) => r.name));
  if (regs.length === 0 || Object.keys(c.regs).some((name) => !known.has(name))) return { reject: 'invalid', t, id };
  if (skewed) return { reject: 'clock_skew', t, id };
  return { change: { meta, id, key, regs } };
}

/** The parent message id of a child row: from the stored row, else from the incoming values or key. */
function parentOf(change: ValidChange, stored: Record<string, SqlValue> | undefined): string | null {
  const col = change.meta.parentMessageColumn;
  if (!col) return null;
  if (stored) return String(stored[col]);
  const k = change.meta.key.indexOf(col);
  if (k >= 0) return String(change.key[k]);
  for (const r of change.regs) if (col in r.v) return String(r.v[col]);
  return null;
}

/**
 * Merge `changes` into `db` (one transaction, triggers silenced). Never throws for a bad change: it is
 * reported in `rejected`. Throws only on a storage failure, which rolls everything back.
 */
export function mergeChanges(db: SqlDriver, changes: readonly unknown[], options: MergeOptions): MergeOutcome {
  if (!Array.isArray(changes)) throw new DigesterError('invalid_input', 'changes must be an array');
  const rejected: Reject[] = [];
  const valid: ValidChange[] = [];
  let maxHlc: string | null = null;
  for (const change of changes) {
    const result = validate(db, change, options);
    if ('reject' in result) {
      rejected.push({ t: result.t as SyncTableName, id: result.id, reason: result.reject });
      continue;
    }
    for (const r of result.change.regs) if (maxHlc === null || r.h > maxHlc) maxHlc = r.h;
    valid.push(result.change);
  }
  // Messages before their children; otherwise keep the sender's order (stable sort).
  valid.sort((a, b) => (TABLE_ORDER.get(a.meta.table) ?? 0) - (TABLE_ORDER.get(b.meta.table) ?? 0));

  let applied = 0;
  let stale = 0;
  const orphans: Orphan[] = [];
  const tables = new Set<SyncTableName>();
  const messageIds = new Set<string>();
  let messageOverflow = false;
  const buckets = new Map<string, BucketRef>();
  /** Messages whose shown scores must be recomputed after the batch. */
  const shownDirty = new Set<string>();
  /** …even when the message has no message_scores row left (a restore). */
  const shownForced = new Set<string>();
  let modelsWritten = false;
  const noteMessage = (id: string): void => {
    if (messageOverflow) return;
    messageIds.add(id);
    if (messageIds.size > MAX_TRACKED_MESSAGE_IDS) messageOverflow = true;
  };
  const noteBucket = (b: { sourceId: string; receivedAt: number } | null): void => {
    if (!b) return;
    const day = Math.floor(b.receivedAt / DAY_MS);
    buckets.set(`${b.sourceId}|${day}`, { sourceId: b.sourceId, day });
  };

  const run = (): void => {
    for (const change of valid) {
      const { meta, id, key } = change;
      let written: string[];
      // The bucket a message counted in before this write (received_at may change with content).
      const before = meta.table === 'messages' ? messageBucketSource(db, String(key[0])) : null;
      try {
        written = db.transaction(() => mergeRow(db, change, rejected, orphans, (n) => (stale += n)));
      } catch (error) {
        if (error instanceof DigesterError) throw error;
        console.log(`[sync] rejected ${meta.table} ${id}: ${error instanceof Error ? error.message : String(error)}`);
        rejected.push({ t: meta.table, id, reason: 'invalid' });
        continue;
      }
      if (written.length === 0) continue;
      applied += written.length;
      tables.add(meta.table);
      if (options.onWritten) for (const grp of written) options.onWritten(meta.table, id, grp);
      if (meta.table === 'models') modelsWritten = true;
      if (meta.table === 'message_scores') shownDirty.add(String(key[0]));
      if (meta.table === 'messages') {
        const messageId = String(key[0]);
        noteMessage(messageId);
        if (written.includes('auto')) shownDirty.add(messageId);
        if (written.some((g) => g !== 'auto')) {
          noteBucket(before);
          noteBucket(messageBucketSource(db, messageId));
        }
        if (messageLife(db, messageId) === 'stub') {
          if (written.includes('life') || written.includes('content')) {
            applyStubCascade(db, messageId);
            for (const child of MESSAGE_CHILD_TABLES) tables.add(child);
          } else if (written.includes('auto')) {
            clearStubShown(db, messageId);
          }
        } else {
          if (written.includes('content') || written.includes('life')) syncFtsRow(db, messageId);
          // A restored message (life written, live now): its rows travel with it (child-set rule), so its
          // shown scores are derived from them even when none are left.
          if (written.includes('life')) shownForced.add(messageId);
        }
      } else {
        const parent = meta.parentMessageColumn ? selectSyncedRow(db, meta, key)?.[meta.parentMessageColumn] : undefined;
        if (parent !== undefined && parent !== null) noteMessage(String(parent));
      }
    }
    if (modelsWritten) {
      let after: string | null = null;
      for (;;) {
        const page = messagesWithSeveralVectors(db, after, 500);
        for (const id of page) shownDirty.add(id);
        if (page.length < 500) break;
        after = page[page.length - 1] as string;
      }
    }
    for (const id of new Set([...shownDirty, ...shownForced])) {
      if (recomputeShownApplied(db, id, { force: shownForced.has(id) })) {
        tables.add('messages');
        noteMessage(id);
      }
    }
  };

  db.transaction(() => {
    withApplying(db, run);
  });

  return {
    applied,
    stale,
    rejected,
    tables: [...tables],
    messageIds: [...messageIds].slice(0, MAX_TRACKED_MESSAGE_IDS),
    messageOverflow,
    maxHlc,
    buckets: [...buckets.values()],
    orphans,
  };
}

/** Merge one row (inside its own savepoint). Returns the names of the registers written. */
function mergeRow(db: SqlDriver, change: ValidChange, rejected: Reject[], orphans: Orphan[], addStale: (n: number) => void): string[] {
  const { meta, id, key, regs } = change;
  const stored = selectSyncedRow(db, meta, key);
  if (meta.parentMessageColumn) {
    const parentId = parentOf(change, stored);
    const life = parentId === null ? 'unknown' : messageLife(db, parentId);
    if (life !== 'live') {
      orphans.push({ t: meta.table, id, parentId: parentId ?? '', parent: life });
      return [];
    }
  }
  if (!stored) {
    if (regs.length !== meta.registers.length) {
      rejected.push({ t: meta.table, id, reason: 'missing_base' });
      return [];
    }
    const values: Record<string, SqlValue> = {};
    meta.key.forEach((col, i) => {
      values[col] = key[i] ?? null;
    });
    for (const r of regs) {
      const spec = meta.registers.find((x) => x.name === r.name);
      if (!spec) continue;
      for (const col of spec.columns) values[col] = r.v[col] ?? null;
      values[spec.hlc] = r.h;
    }
    insertSyncedRow(db, meta, values);
    return regs.map((r) => r.name);
  }
  const written: string[] = [];
  let staleHere = 0;
  for (const r of regs) {
    const spec = meta.registers.find((x) => x.name === r.name);
    if (!spec) continue;
    const current = String(stored[spec.hlc] ?? '');
    if (r.h > current) {
      updateSyncedRegister(db, meta, key, spec, r.v, r.h);
      written.push(r.name);
    } else {
      staleHere++;
    }
  }
  addStale(staleHere);
  return written;
}

/**
 * contracts/wiring.ts `ApplyChanges`: the merge, reporting the contract's ApplyResult. Child rows of a
 * stub or unknown message count as stale here (sync.md §5 step 5).
 */
export const applyChanges: ApplyChanges = (db, changes, options) => {
  const outcome = mergeChanges(db, changes, options);
  return {
    applied: outcome.applied,
    stale: outcome.stale + outcome.orphans.length,
    rejected: outcome.rejected,
    tables: outcome.tables,
    messageIds: outcome.messageIds,
  };
};
